import { useCallback } from 'react'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Loading from '@/elements/Loading'
import FormSearchableSelect from '@/elements/FormSearchableSelect'
import Table from '@/components/Table'
import Pagination from '@/components/Pagination'
import Modal from '@/components/Modal'
import useBreakpoints from '@/hooks/useBreakpoints'
import { useIndividualDelegatesTable } from '@/hooks/tables/individualDelegatesTable'
import useFiltering from '@/hooks/useFiltering'
import { useAssignments } from '@/hooks/useAssignments'
import type { IndividualAssignmentResponse } from '@/api/types/Organization'
import type { SubjectResponse } from '@/api/types/Subject'

function IndividualDelegates({ individualId }: { individualId: SubjectResponse['id'] }) {
  const { isTablet, isMobile } = useBreakpoints()
  const { updateFilterBy, allFilters } = useFiltering<['name']>()

  const {
    assignments,
    availableAssignments: availableDelegates,
    pagination,
    isLoadingAssignments,
    isLoadingAvailableAssignments: isLoadingDelegates,
    setPage,
    handleCreate,
    handleDelete,
    openDeleteModal,
    closeDeleteModal,
    showDeleteModal
  } = useAssignments({
    userId: individualId,
    type: 'DELEGATE',
    paginationKey: 'delegates',
    successMessages: {
      create: 'Delegate assigned successfully',
      delete: 'Delegate unassigned successfully'
    },
    errorMessages: {
      create: 'Failed to assign delegate',
      delete: 'Failed to unassign delegate'
    }
  })

  const delegatesTableData = useIndividualDelegatesTable(
    assignments as IndividualAssignmentResponse[],
    openDeleteModal
  )

  const handleAssignDelegate = useCallback(async (delegate: IndividualAssignmentResponse | null) => {
    if (!delegate?.id) return
    await handleCreate({
      type: 'DELEGATE',
      targetUserId: delegate.id
    })
  }, [handleCreate])

  return (
    <Flex wfull inline={!isTablet} gap={4} wrap>
      <Flex fraction={4} gap={2} wfull>
        <Text variant='h3'>Delegates</Text>
        <Text color='black-light-40'>
          Update individual's assigned delegates.
        </Text>
      </Flex>
      <Flex fraction={6} wfull hfull gap={6}>
        <FormSearchableSelect<IndividualAssignmentResponse>
          name='delegatesSelect'
          isUserSearch
          autoSubmit={false}
          options={availableDelegates as IndividualAssignmentResponse[]}
          submitLabel='Assign'
          placeholder='Search delegates...'
          optionLabel={item => item.details.fullName}
          optionValue={item => item.id}
          optionImage={item => item.assets?.PROFILE_PICTURE || ''}
          updateSearch={q => updateFilterBy('name', q)}
          searchResult={allFilters.name || ''}
          loadingOptions={isLoadingDelegates}
          onSubmit={handleAssignDelegate}
        />
        {isLoadingAssignments ? (
          <Flex align='center' justify='center' wfull py={8}>
            <Loading />
          </Flex>
        ) : (
          <Flex wfull align='center' gap={3}>
            <Table {...delegatesTableData} />
            <Modal
              isOpen={showDeleteModal}
              title='Remove Delegate'
              onSave={handleDelete}
              onCancel={closeDeleteModal}
              cancelLabel='No'
              saveLabel='Yes'
            >
              <Text color='black' style={{ hyphens: 'none' }}>
                Are you sure you want to remove this delegate? This action cannot be undone.
              </Text>
            </Modal>
            <Pagination
              variant={isMobile ? 'compact' : 'default'}
              onPageChange={setPage}
              {...pagination}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

export default IndividualDelegates
